import React, {
  Children,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { IBpTabProps } from './tab/BpTab'
import { NavItem } from '../../cards/nav-item/NavItem'
import { BpButton } from '../../buttons/bp-button/BpButton'
import { IconArrowBack } from '../../icons/IconArrowBack'

type BpTab = ReactElement<PropsWithChildren<IBpTabProps>>

export interface BpTabsProps {
  children: BpTab | BpTab[]
  active?: string
  onToggle?: (activeTab: string) => void
  onChange?: (index: string) => void
  className?: string
  back?: () => void
  selector?: boolean
  size?: 'xs' | 'md' | 'lg'
  variant?: 'default' | 'row'
  align?: 'left' | 'center' | 'right'
}

export const BpTabs = (props: BpTabsProps) => {
  const {
    children,
    className = '',
    size = 'lg',
    variant = 'default',
    align = 'left',
    active,
    onToggle,
    onChange,
  } = props
  const [panel, setPanel] = useState<ReactNode | undefined>()
  const [activeTab, setActiveTab] = useState<number>(0)

  useEffect(() => {
    if (active) {
      Children.forEach(children, (child, index) => {
        if (child.props.activeKey === active) {
          setActiveTab(index)
        } else if (child.props.title === active) {
          setActiveTab(index)
        }
      })
    }
  }, [active])

  useEffect(() => {
    Children.forEach(children, (child, index) => {
      if (index === activeTab) setPanel(child.props.panel)
    })
  }, [activeTab, children])

  return (
    <>
      <div
        className={`flex flex-col justify-start gap-[12px] lg:flex-row lg:items-center ${
          variant === 'row' && 'shadow-row-shadow'
        } ${className}`}
      >
        {props.back && (
          <BpButton
            className='mr-1'
            contentClassName='md:hidden'
            iconLeft={<IconArrowBack />}
            size='sm'
            theme='outline'
            onClick={props.back}
          >
            Go back
          </BpButton>
        )}
        <ul
          className={`scrollbar-hide mr-auto flex w-full max-w-full overflow-y-auto lg:pl-[24px] ${{ left: 'justify-start', center: 'justify-center', right: 'justify-end' }[align]} ${{ xs: 'gap-[16px]', md: 'gap-[16px] lg:pl-0', lg: 'gap-[32px]' }[size]} `}
        >
          {Children.map(children, (child, index) => {
            if (child?.props.hide) return null

            return (
              <li className={child.props.disabled ? 'pointer-events-none opacity-40' : ''}>
                <NavItem
                  active={index == activeTab}
                  className={'flex flex-col items-center gap-[6px]'}
                  decoration={variant === 'row' ? 'rounded' : 'line'}
                  size={size}
                  theme='dark'
                  // по клику, а не по mousedown: на mousedown снаружи закрываются дропдауны, а поповер
                  // фильтров откатывает неприменённые правки — вкладка должна применять фильтр после этого
                  onClick={() => {
                    if (child.props.disabled) return

                    const key = child.props.activeKey || child.props.title

                    if (onChange) {
                      onChange(key)
                    } else {
                      setActiveTab(index)
                      onToggle?.(key)
                    }
                  }}
                >
                  <div className={'flex items-center gap-[6px]'}>
                    <div className={'font-medium'}>{child?.props.title}</div>
                    {!!child?.props.counter && (
                      <div
                        className={`flex h-[22px] items-center ${
                          index == activeTab
                            ? 'bg-[#A059FF] text-white'
                            : 'bg-[#E8EDF2] text-[#6684A7]'
                        } rounded-[4px] px-[6px] text-[12px] font-medium`}
                      >
                        {child?.props.counter}
                      </div>
                    )}
                  </div>
                </NavItem>
              </li>
            )
          })}
        </ul>
        {panel && <div className={'pb-[24px] lg:pb-[0px]'}>{panel}</div>}
      </div>
      {!props.selector && (
        <section className='flex-1'>{Children.toArray(children)[activeTab]}</section>
      )}
    </>
  )
}
